
import { useNavigate, useSearchParams } from "react-router-dom";
import { RiderLayout } from "@/components/rider/RiderLayout";
import { RiderLandingForm } from "@/components/rider/landing/RiderLandingForm";
import { motion, useAnimationControls } from "framer-motion";
import { RiderLandingFormData } from "@/types/rider";
import { ReviewSection } from "@/components/rider/landing/ReviewSection";
import { getAssetPath } from "@/utils/assetUtils";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Toaster } from "@/components/ui/toaster";
import { SplashScreen } from "@/components/SplashScreen";

const RiderLanding = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const discoBallControls = useAnimationControls();
  const [isAnimating, setIsAnimating] = useState(false);
  const [showSplash, setShowSplash] = useState(false);

  useEffect(() => {
    // Check if splash screen has been shown in this session
    const hasSeenSplash = sessionStorage.getItem('hasSeenSplash');
    if (!hasSeenSplash) {
      setShowSplash(true);
    }

    // Check for promo_code query parameter and save to cookie
    const promoCode = searchParams.get('promo_code');
    if (promoCode) {
      // Save promo code to cookie with 3 day expiration
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 3);
      document.cookie = `promo_code=${encodeURIComponent(promoCode)}; expires=${expirationDate.toUTCString()}; path=/`;
      console.log('Promo code saved to cookie:', promoCode);
    }
  }, [searchParams]);

  const handleSplashComplete = () => {
    setShowSplash(false);
    sessionStorage.setItem('hasSeenSplash', 'true');
  };
  
  const handleFormSubmit = async (formData: RiderLandingFormData, price: number) => {
    // Start animation
    setIsAnimating(true);
    
    // Animate disco ball driving off the screen
    await discoBallControls.start({
      x: window.innerWidth,
      transition: { duration: 0.8, ease: "easeInOut" }
    });
    
    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append("destination", formData.destination);
    queryParams.append("capacity", formData.capacity.toString());
    queryParams.append("duration", formData.duration.toString());
    queryParams.append("isRoundTrip", formData.isRoundTrip.toString());
    queryParams.append("price", price.toString());
    
    // Add place_id if available
    if (formData.placeId) {
      queryParams.append("placeId", formData.placeId);
    }
    
    // Add date if available
    if (formData.date) {
      queryParams.append("date", format(formData.date, "yyyy-MM-dd"));
    }
    
    // Add startTime if available (default to 17:00 / 5 PM)
    queryParams.append("startTime", formData.startTime || "17:00");
    
    // Navigate to the quote page with form data as query params and scroll to top on mobile
    navigate(`/rider-quote?${queryParams.toString()}`);
    
    // Scroll to top on mobile for better UX
    if (window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      <RiderLayout>
        <div 
          className="min-h-screen bg-cover bg-center flex flex-col items-center justify-center px-4 py-10"
          style={{ 
            backgroundImage: "url('UltraBackground.jpg')",
            backgroundColor: "rgba(0,0,0,0.75)",
            backgroundBlendMode: "overlay"
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8 pt-23" // Adjusted from pt-18 to pt-23 (moved down 5px)
          >
            <h2 className="text-white text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              Book Party Buses Instantly
            </h2>
            <motion.div 
              className="flex justify-center"
              animate={discoBallControls}
            >
              <img 
                src={getAssetPath("PartyBusDiscoBall.png")} 
                alt="Disco Ball" 
                className="h-16 md:h-20 lg:h-24"
              />
            </motion.div>
          </motion.div>
          
          {!isAnimating && <RiderLandingForm onSubmit={handleFormSubmit} />}
          
          {!isAnimating && <ReviewSection />}
        </div>
        <Toaster />
      </RiderLayout>
    </>
  );
};

export default RiderLanding;
